import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash.merge';
import { Provider as ReduxProvider } from 'react-redux';

import { createRenderer } from 'fela';
import {
  RendererProvider as FelaProvider,
  ThemeProvider,
} from 'react-fela';
import placeholderPrefixer from 'fela-plugin-placeholder-prefixer';
import webPreset from 'fela-preset-web';

import createStore from './createStore';
import { PlayerContext } from './PlayerContext';
import { OptionsContext, optionsShape } from './OptionsContext';

import SyncPlayerState from './SyncPlayerState';
import SyncMiniPlayerState from './SyncMiniPlayerState';
import LocaleProvider from './Player/locale/LocaleProvider';
import Player from './Player/Player';
import { defaultTheme } from './Player/theme';

function PlayerUI({ player, options }) {
  const [renderer] = useState(() => options.felaRenderer || createRenderer({
    plugins: [
      placeholderPrefixer(),
      ...webPreset,
    ],
    selectorPrefix: 'hp_',
  }));

  const [store] = useState(() => createStore(player));

  const theme = useMemo(() => merge({}, defaultTheme, options.theme, {
    direction: 'ltr', // always left-to-right direction
    origDirection: options.theme?.direction || 'ltr',
  }), []);

  useEffect(() => () => {
    // destroy Fela styles if we create own renderer
    if (!options.felaRenderer) {
      renderer.clear();
    }
  }, []);

  return (
    <FelaProvider renderer={renderer}>
      <ThemeProvider theme={theme}>
        <LocaleProvider
          locale={options.language}
          providedMessages={options.messages}
        >
          <ReduxProvider store={store}>
            <PlayerContext.Provider value={player}>
              <OptionsContext.Provider value={options}>
                <SyncPlayerState player={player} />
                <SyncMiniPlayerState player={player} />

                <Player />
              </OptionsContext.Provider>
            </PlayerContext.Provider>
          </ReduxProvider>
        </LocaleProvider>
      </ThemeProvider>
    </FelaProvider>
  );
}

PlayerUI.propTypes = {
  player: PropTypes.object.isRequired,
  options: optionsShape.isRequired,
};

export default React.memo(PlayerUI);
